// Multipart upload with progress. Parts go straight to R2 (presigned URLs) or through the Worker (local dev).
// XMLHttpRequest is used because fetch cannot report upload progress.
import { api, API_URL } from "../api";

export type UploadProgress = { sent: number; total: number; bytesPerSecond: number; partsDone: number; parts: number };

const CONCURRENCY = 4;
const RETRIES = 3;

function putPart(url: string, blob: Blob, onProgress: (loaded: number) => void, signal: AbortSignal): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("etag");
        if (!etag) return reject(new Error("Storage did not return an ETag. Check the bucket CORS settings (ExposeHeaders ETag)."));
        resolve(etag);
      } else reject(new Error(`Part upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error while uploading"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
}

export async function uploadDrawing(projectId: string, file: File, onProgress: (p: UploadProgress) => void, signal: AbortSignal) {
  const start = await api.startUpload(projectId, file.name, file.size);
  const loaded = new Map<number, number>();
  const etags: { part_number: number; etag: string }[] = [];
  const t0 = performance.now();
  let next = 0;
  const report = () => {
    const sent = [...loaded.values()].reduce((a, b) => a + b, 0);
    const secs = Math.max(0.25, (performance.now() - t0) / 1000);
    onProgress({ sent, total: file.size, bytesPerSecond: sent / secs, partsDone: etags.length, parts: start.parts.length });
  };
  const worker = async () => {
    while (next < start.parts.length) {
      const part = start.parts[next++];
      const from = (part.part_number - 1) * start.part_size;
      const blob = file.slice(from, Math.min(file.size, from + start.part_size));
      const url = new URL(part.url, API_URL).toString();
      for (let attempt = 1; ; attempt++) {
        try {
          const etag = await putPart(url, blob, (n) => { loaded.set(part.part_number, n); report(); }, signal);
          loaded.set(part.part_number, blob.size);
          etags.push({ part_number: part.part_number, etag });
          report();
          break;
        } catch (e) {
          if (signal.aborted || attempt >= RETRIES) throw e;
          loaded.set(part.part_number, 0);
          await new Promise((r) => setTimeout(r, 1000 * attempt));
        }
      }
    }
  };
  try {
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, start.parts.length) }, worker));
  } catch (e) {
    api.abortUpload(projectId, start.upload_id).catch(() => undefined);
    throw e;
  }
  return api.completeUpload(projectId, start.upload_id, etags);
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}
